import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider, usePermissions } from './context/PermissionContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Suppliers from './pages/Suppliers';
import Clients from './pages/Clients';
import SalesQuotations from './pages/SalesQuotations';
import ServiceQuotations from './pages/ServiceQuotations';
import Purchases from './pages/Purchases';
import SalesCases from './pages/SalesCases';
import ServiceCases from './pages/ServiceCases';
import Tenders from './pages/Tenders';
import Accounting from './pages/Accounting';
import Users from './pages/Users';
import Permissions from './pages/Permissions';
import CompanySettings from './pages/CompanySettings';
import DirectorDashboard from './pages/DirectorDashboard';
import NotificationBell from './components/NotificationBell';
import InquiriesBadge from './components/InquiriesBadge';
import Warehouse from './pages/Warehouse';
import ServiceModule from './pages/ServiceModule';
import CounterpartyStatement from './pages/CounterpartyStatement';
import Tasks from './pages/Tasks';
import Inquiries from './pages/Inquiries';

interface NavItem {
  to?: string;
  label: string;
  icon: string;
  module?: string;
  directorOnly?: boolean;
  // Module/permission ruxsatiga bog'liq emas (Inquiries kompaniya bo'yicha
  // umumiy, bo'lim-scope yo'q) - qat'iy rol ro'yxati bilan ko'rinishi.
  roles?: string[];
  badge?: React.ReactNode;
  children?: { to: string; label: string; icon: string; module?: string; adminOnly?: boolean }[];
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '📊', module: 'dashboard' },
  { to: '/director-dashboard', label: 'Direktor paneli', icon: '🏢', directorOnly: true },
  // Shaxsiy vazifalar (Model P) - `module` YO'Q qasddan: bo'lim/rol
  // ruxsatiga bog'liq emas, har bir hodim (shu jumladan admin/direktor)
  // o'ziga tegishli vazifalarni ko'radi - shuning uchun HAMMA uchun ko'rinadi.
  { to: '/tasks', label: 'Vazifalarim', icon: '✅' },
  // Inquiries/Inbox - kompaniya bo'yicha umumiy (bo'lim-scope yo'q), shuning
  // uchun `module` emas qat'iy `roles` ro'yxati bilan cheklangan (qarang
  // backend INQUIRY_STAFF_ROLES).
  { to: '/inquiries', label: "So'rovlar", icon: '📥', roles: ['admin', 'director', 'sales_head', 'sales_manager'], badge: <InquiriesBadge /> },
  {
    label: 'Katalog', icon: '📦',
    children: [
      { to: '/products', label: 'Mahsulotlar', icon: '🧪', module: 'products' },
      { to: '/clients', label: 'Mijozlar', icon: '👔', module: 'clients' },
    ]
  },
  {
    label: 'Savdo', icon: '💼',
    children: [
      { to: '/sales-cases', label: 'Sales Cases', icon: '💼', module: 'sales_cases' },
      { to: '/sales-quotations', label: 'Sotuv takliflari', icon: '📈', module: 'sales_quotations' },
      { to: '/tenders', label: 'Tenderlar', icon: '🏛️', module: 'tenders' },
    ]
  },
  {
    label: 'Servis', icon: '🔧',
    children: [
      { to: '/service-cases', label: 'Service Cases', icon: '🔧', module: 'service_cases' },
      { to: '/service-quotations', label: 'Xizmat takliflari', icon: '🛠️', module: 'service_quotations' },
      { to: '/service-module', label: 'Instrument Registry', icon: '🔬', module: 'service_module' },
    ]
  },
  {
    label: 'Xarid', icon: '🛒',
    children: [
      { to: '/suppliers', label: 'Yetkazib beruvchilar', icon: '🏭', module: 'suppliers' },
      { to: '/purchases', label: "Xarid so'rovlari", icon: '📋', module: 'purchases' },
    ]
  },
  {
    label: 'Ombor', icon: '🪣',
    children: [
      { to: '/warehouse', label: 'Ombor boshqaruvi', icon: '📦', module: 'warehouse' },
    ]
  },
  {
    label: 'Moliya', icon: '💰',
    children: [
      { to: '/accounting', label: 'Buxgalteriya', icon: '📒', module: 'accounting' },
      { to: '/counterparty-statement', label: 'Kontragent hisoboti', icon: '📊', module: 'accounting' },
    ]
  },
  {
    label: 'Sozlamalar', icon: '⚙️',
    children: [
      { to: '/users', label: 'Foydalanuvchilar', icon: '👥', module: 'users' },
      { to: '/permissions', label: 'Ruxsatlar', icon: '🔐', module: 'users' },
      { to: '/company-settings', label: "Kompaniya ma'lumotlari", icon: '🏢', adminOnly: true },
    ]
  },
];

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  director: 'Direktor',
  sales_head: "Savdo bo'limi rahbari",
  sales_manager: 'Savdo menejeri',
  service_head: "Servis bo'limi rahbari",
  service_engineer: 'Servis injener',
  warehouse_head: 'Ombor rahbari',
  logistic_head: "Logistika bo'limi boshlig'i",
  logist: 'Logist',
  chief_accountant: 'Bosh buxgalter',
  accountant: 'Buxgalter',
  hr_manager: 'HR menejeri',
  lawyer: 'Yurist',
};

// Desktop dropdown menyu
const DropdownMenu: React.FC<{ item: NavItem; onClose?: () => void }> = ({ item, onClose }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const { canView } = usePermissions();
  const { user } = useAuth();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const visibleChildren = item.children?.filter(c => c.adminOnly ? (user?.role === 'admin' || user?.role === 'director') : canView(c.module!)) || [];
  if (visibleChildren.length === 0) return null;
  const isActive = visibleChildren.some(c => location.pathname === c.to);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-sm font-medium transition px-2 py-1 rounded-lg ${isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'}`}>
        <span>{item.icon}</span>
        <span>{item.label}</span>
        <span className={`text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50 min-w-48">
          {visibleChildren.map(child => (
            <Link key={child.to} to={child.to} onClick={() => { setOpen(false); onClose && onClose(); }}
              className={`flex items-center gap-2 px-4 py-2 text-sm transition ${location.pathname === child.to ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
              <span>{child.icon}</span>
              <span>{child.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Mobile yon menyu
const MobileSidebar: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const { canView } = usePermissions();
  const location = useLocation();
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  useEffect(() => { onClose(); }, [location.pathname]);

  return (
    <>
      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 w-72 bg-white shadow-2xl z-50 transform transition-transform duration-300 flex flex-col h-viewport-safe ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Header */}
        <div className="shrink-0 bg-blue-600 px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold text-sm">L</span>
            </div>
            <div>
              <p className="text-white font-bold">Lab System</p>
              <p className="text-blue-200 text-xs">{user?.full_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white text-2xl leading-none">×</button>
        </div>

        {/* Rol */}
        <div className="shrink-0 px-4 py-2 bg-blue-50 border-b border-blue-100">
          <p className="text-xs text-blue-600 font-medium">{roleLabels[user?.role || ''] || user?.role}</p>
        </div>

        {/* Nav items — mustaqil skroll qilinadigan o'rta qism */}
        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {navItems.map((item, i) => {
            if (item.to) {
              if (item.directorOnly && user?.role !== 'admin' && user?.role !== 'director') return null;
              if (item.module && !canView(item.module)) return null;
              if (item.roles && !item.roles.includes(user?.role || '')) return null;
              return (
                <Link key={i} to={item.to}
                  className={`flex items-center gap-3 px-4 py-3 text-sm border-b border-gray-50 ${location.pathname === item.to ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <span className="text-lg">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  {item.badge}
                </Link>
              );
            }

            const visibleChildren = item.children?.filter(c => c.adminOnly ? (user?.role === 'admin' || user?.role === 'director') : canView(c.module!)) || [];
            if (visibleChildren.length === 0) return null;
            const isGroupActive = visibleChildren.some(c => location.pathname === c.to);
            const isExpanded = expandedGroup === item.label;

            return (
              <div key={i}>
                <button
                  onClick={() => setExpandedGroup(isExpanded ? null : item.label)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm border-b border-gray-50 ${isGroupActive ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <span className={`text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
                </button>
                {isExpanded && (
                  <div className="bg-gray-50">
                    {visibleChildren.map(child => (
                      <Link key={child.to} to={child.to}
                        className={`flex items-center gap-3 px-8 py-3 min-h-[44px] text-sm border-b border-gray-100 ${location.pathname === child.to ? 'text-blue-600 font-medium bg-blue-50' : 'text-gray-600 hover:bg-gray-100'}`}>
                        <span>{child.icon}</span>
                        <span>{child.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Chiqish — doim ko'rinadi, skroll qilinmaydi */}
        <div className="shrink-0 p-4 bg-white border-t border-gray-200" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <button onClick={() => { logout(); onClose(); }}
            className="w-full bg-red-50 text-red-600 border border-red-200 rounded-lg py-2 min-h-[44px] text-sm font-medium hover:bg-red-100">
            Chiqish
          </button>
        </div>
      </div>
    </>
  );
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { canView } = usePermissions();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow px-4 py-3 flex justify-between items-center">
        {/* Logo + Desktop nav */}
        <div className="flex items-center gap-2">
          {/* Hamburger (mobile) */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden flex flex-col gap-1 p-1 mr-2"
          >
            <span className="block w-5 h-0.5 bg-gray-600"></span>
            <span className="block w-5 h-0.5 bg-gray-600"></span>
            <span className="block w-5 h-0.5 bg-gray-600"></span>
          </button>

          <div className="flex items-center gap-2 mr-4">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="text-lg font-bold text-blue-700">Lab System</span>
          </div>

          {/* Desktop navigatsiya */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item, i) => {
              if (item.to) {
                if (item.directorOnly && user?.role !== 'admin' && user?.role !== 'director') return null;
                if (item.module && !canView(item.module)) return null;
                if (item.roles && !item.roles.includes(user?.role || '')) return null;
                return (
                  <Link key={i} to={item.to}
                    className={`flex items-center gap-1 text-sm font-medium transition px-2 py-1 rounded-lg ${location.pathname === item.to ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'}`}>
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                    {item.badge}
                  </Link>
                );
              }
              return <DropdownMenu key={i} item={item} />;
            })}
          </div>
        </div>

        {/* O'ng tomon */}
        <div className="flex items-center gap-2">
          <NotificationBell />
          <div className="hidden sm:block text-right">
            <p className="text-sm font-medium text-gray-800">{user?.full_name}</p>
            <p className="text-xs text-gray-500">{roleLabels[user?.role || ''] || user?.role}</p>
          </div>
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-bold text-sm">{user?.full_name?.charAt(0).toUpperCase()}</span>
          </div>
          <button onClick={logout}
            className="hidden sm:block text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 px-2 py-1 rounded-lg transition">
            Chiqish
          </button>
        </div>
      </nav>

      {/* Mobile sidebar */}
      <MobileSidebar open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      <div>{children}</div>
    </div>
  );
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-gray-500">Yuklanmoqda...</div></div>;
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const ModuleRoute: React.FC<{ children: React.ReactNode; module: string }> = ({ children, module }) => {
  const { canView, loading } = usePermissions();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-gray-500">Yuklanmoqda...</div></div>;
  if (!canView(module)) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
          <span className="text-6xl mb-4">🔒</span>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Kirish huquqi yo'q</h2>
          <p className="text-gray-500">Sizda bu bo'limga kirish uchun ruxsat mavjud emas.</p>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
};

const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'admin' && user?.role !== 'director') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
          <span className="text-6xl mb-4">🔒</span>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Faqat admin uchun</h2>
          <p className="text-gray-500">Bu sahifaga faqat admin kira oladi.</p>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
};

const INQUIRY_STAFF_ROLES = ['admin', 'director', 'sales_head', 'sales_manager'];

const InquiryAccessRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!INQUIRY_STAFF_ROLES.includes(user?.role || '')) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
          <span className="text-6xl mb-4">🔒</span>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Kirish huquqi yo'q</h2>
          <p className="text-gray-500">Sizda bu bo'limga kirish uchun ruxsat mavjud emas.</p>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
};

const DirectorOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'admin' && user?.role !== 'director') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
          <span className="text-6xl mb-4">🔒</span>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Faqat direktor uchun</h2>
          <p className="text-gray-500">Bu sahifaga faqat direktor va admin kira oladi.</p>
        </div>
      </Layout>
    );
  }
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={<PrivateRoute><ModuleRoute module="dashboard"><Dashboard /></ModuleRoute></PrivateRoute>} />
            <Route path="/products" element={<PrivateRoute><ModuleRoute module="products"><Products /></ModuleRoute></PrivateRoute>} />
            <Route path="/suppliers" element={<PrivateRoute><ModuleRoute module="suppliers"><Suppliers /></ModuleRoute></PrivateRoute>} />
            <Route path="/clients" element={<PrivateRoute><ModuleRoute module="clients"><Clients /></ModuleRoute></PrivateRoute>} />
            <Route path="/sales-quotations" element={<PrivateRoute><ModuleRoute module="sales_quotations"><SalesQuotations /></ModuleRoute></PrivateRoute>} />
            <Route path="/service-quotations" element={<PrivateRoute><ModuleRoute module="service_quotations"><ServiceQuotations /></ModuleRoute></PrivateRoute>} />
            <Route path="/tenders" element={<PrivateRoute><ModuleRoute module="tenders"><Tenders /></ModuleRoute></PrivateRoute>} />
            <Route path="/purchases" element={<PrivateRoute><ModuleRoute module="purchases"><Purchases /></ModuleRoute></PrivateRoute>} />
            <Route path="/sales-cases" element={<PrivateRoute><ModuleRoute module="sales_cases"><SalesCases /></ModuleRoute></PrivateRoute>} />
            <Route path="/service-cases" element={<PrivateRoute><ModuleRoute module="service_cases"><ServiceCases /></ModuleRoute></PrivateRoute>} />
            <Route path="/warehouse" element={<PrivateRoute><ModuleRoute module="warehouse"><Warehouse /></ModuleRoute></PrivateRoute>} />
            <Route path="/service-module" element={<PrivateRoute><ModuleRoute module="service_module"><ServiceModule /></ModuleRoute></PrivateRoute>} />
            <Route path="/accounting" element={<PrivateRoute><ModuleRoute module="accounting"><Accounting /></ModuleRoute></PrivateRoute>} />
            <Route path="/counterparty-statement" element={<PrivateRoute><ModuleRoute module="accounting"><CounterpartyStatement /></ModuleRoute></PrivateRoute>} />
            <Route path="/users" element={<PrivateRoute><ModuleRoute module="users"><Users /></ModuleRoute></PrivateRoute>} />
            <Route path="/permissions" element={<PrivateRoute><ModuleRoute module="users"><Permissions /></ModuleRoute></PrivateRoute>} />
            <Route path="/company-settings" element={<PrivateRoute><AdminOnlyRoute><CompanySettings /></AdminOnlyRoute></PrivateRoute>} />
            {/* Shaxsiy vazifalar - ModuleRoute'siz qasddan: bo'lim/rol
                ruxsatiga bog'liq emas, faqat login qilingan bo'lishi kifoya
                (xavfsizlik chegarasi backendda owner_id filtri - App.tsx'dagi
                route gate emas). */}
            <Route path="/tasks" element={<PrivateRoute><Layout><Tasks /></Layout></PrivateRoute>} />
            <Route path="/inquiries" element={<PrivateRoute><InquiryAccessRoute><Inquiries /></InquiryAccessRoute></PrivateRoute>} />
            <Route path="/director-dashboard" element={<PrivateRoute><DirectorOnlyRoute><Layout><DirectorDashboard /></Layout></DirectorOnlyRoute></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;
